import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Wifi, Car, Coffee, Sun, Waves, TreePine, Wind, Tv, MapPin, Star, ArrowRight, ChevronLeft, ChevronRight, Play, X, Calendar, Users, Award, Clock } from 'lucide-react';

import { BASE_URL as API } from '../lib/api';

const translations = {
  it: {
    welcome: 'Benvenuti a',
    heroTitle: 'La Maisonette',
    heroLocation: 'di Paestum',
    heroSubtitle: 'Due casette gemelle immerse nella magia dei templi greci',
    locationBadge: 'A 20 passi dalle mura dell\'Antica Città di Paestum',
    discover: 'Scopri',
    ourStructure: 'La Nostra Struttura',
    viewAll: 'Vedi Tutte le Foto',
    noImages: 'Nessuna immagine disponibile',
    comfort: 'I Nostri Comfort',
    ourServices: 'Servizi Inclusi',
    noServices: 'Nessun servizio disponibile',
    ourStory: 'La Nostra Storia',
    uniqueExperience: 'Un\'Esperienza Unica tra Storia e Natura',
    storyP1: 'Le nostre due casette gemelle sono il rifugio perfetto per chi cerca pace e bellezza. Situate a pochi passi dal Parco Archeologico di Paestum, patrimonio UNESCO, offrono il perfetto equilibrio tra il fascino della storia millenaria e il comfort moderno.',
    storyP2: 'Con soffitti in legno a vista, arredi curati nei minimi dettagli e un\'atmosfera calda e accogliente, ogni soggiorno diventa un\'esperienza indimenticabile. Il nostro giardino privato è l\'ideale per rilassarsi dopo una giornata tra templi e spiagge.',
    bookYourStay: 'Prenota il Tuo Soggiorno',
    whereWeAre: 'Dove Siamo',
    ourLocation: 'La Nostra Posizione',
    locationDesc: 'Capaccio Paestum (SA) - Nel cuore della Magna Grecia',
    temples: 'Templi di Paestum',
    beach: 'Spiaggia',
    salerno: 'Salerno',
    walkMin: '5 minuti a piedi',
    carMin10: '10 minuti in auto',
    carMin30: '30 minuti in auto',
    stats: {
      guests: 'Ospiti Felici',
      rating: 'Rating Booking.com',
      yearsExp: 'Anni di Esperienza',
      reviews: 'Recensioni'
    },
    whyUs: 'Perché Sceglierci',
    whyUsSubtitle: 'L\'eccellenza dell\'ospitalità campana',
    reason1Title: 'Posizione Unica',
    reason1Desc: 'A pochi passi dai templi greci meglio conservati d\'Italia',
    reason2Title: 'Comfort Autentico', 
    reason2Desc: 'Casette tradizionali con tutti i comfort moderni',
    reason3Title: 'Ospitalità Vera',
    reason3Desc: 'Accoglienza familiare con attenzione ai dettagli',
    ctaTitle: 'Pronto per un\'Esperienza Indimenticabile?',
    ctaSubtitle: 'Prenota ora e vivi la magia della Magna Grecia',
    checkAvailability: 'Verifica Disponibilità',
    guestsSay: 'Le Voci dei Nostri Ospiti',
    verifiedReviews: 'Recensioni Verificate'
  },
  en: {
    welcome: 'Welcome to',
    heroTitle: 'La Maisonette',
    heroLocation: 'di Paestum',
    heroSubtitle: 'Twin cottages immersed in the magic of Greek temples',
    locationBadge: '20 steps from the walls of the Ancient City of Paestum',
    discover: 'Discover',
    ourStructure: 'Our Property',
    viewAll: 'View All Photos',
    noImages: 'No images available',
    comfort: 'Our Comforts',
    ourServices: 'Included Services',
    noServices: 'No services available',
    ourStory: 'Our Story',
    uniqueExperience: 'A Unique Experience Between History and Nature',
    storyP1: 'Our twin cottages are the perfect retreat for those seeking peace and beauty. Located just steps from the Paestum Archaeological Park, a UNESCO heritage site, they offer the perfect balance between the charm of ancient history and modern comfort.',
    storyP2: 'With exposed wooden ceilings, carefully curated furnishings, and a warm, welcoming atmosphere, every stay becomes an unforgettable experience. Our private garden is ideal for relaxing after a day among temples and beaches.',
    bookYourStay: 'Book Your Stay',
    whereWeAre: 'Location',
    ourLocation: 'Our Location',
    locationDesc: 'Capaccio Paestum (SA) - In the heart of Magna Graecia',
    temples: 'Temples of Paestum',
    beach: 'Beach',
    salerno: 'Salerno',
    walkMin: '5 minutes walk',
    carMin10: '10 minutes by car',
    carMin30: '30 minutes by car',
    stats: {
      guests: 'Happy Guests',
      rating: 'Booking.com Rating',
      yearsExp: 'Years of Experience',
      reviews: 'Reviews'
    },
    whyUs: 'Why Choose Us',
    whyUsSubtitle: 'Excellence in Campanian hospitality',
    reason1Title: 'Unique Location',
    reason1Desc: 'Steps away from the best preserved Greek temples in Italy',
    reason2Title: 'Authentic Comfort',
    reason2Desc: 'Traditional cottages with all modern comforts',
    reason3Title: 'True Hospitality',
    reason3Desc: 'Family welcome with attention to every detail',
    ctaTitle: 'Ready for an Unforgettable Experience?',
    ctaSubtitle: 'Book now and live the magic of Magna Graecia',
    checkAvailability: 'Check Availability',
    guestsSay: 'What Our Guests Say',
    verifiedReviews: 'Verified Reviews'
  }
};

const ICON_MAP = {
  wifi: Wifi,
  car: Car,
  coffee: Coffee,
  sun: Sun,
  waves: Waves,
  tree: TreePine,
  wind: Wind,
  tv: Tv
};

export default function AboutPage() {
  const { language } = useLanguage();
  const t = translations[language] || translations.it;
  const [gallery, setGallery] = useState([]);
  const [amenities, setAmenities] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentReview, setCurrentReview] = useState(0);
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const galleryRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-slide for hero gallery
  useEffect(() => {
    if (gallery.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % gallery.length);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [gallery.length]);

  // Auto-rotate reviews
  useEffect(() => {
    if (reviews.length > 1) {
      const interval = setInterval(() => {
        setCurrentReview((prev) => (prev + 1) % reviews.length);
      }, 6000);
      return () => clearInterval(interval);
    }
  }, [reviews.length]);

  const fetchData = async () => {
    try {
      const [galleryRes, amenitiesRes, reviewsRes] = await Promise.all([
        fetch(`${API}/api/gallery`),
        fetch(`${API}/api/amenities`),
        fetch(`${API}/api/reviews`)
      ]);
      
      if (galleryRes.ok) {
        const galleryData = await galleryRes.json();
        setGallery(galleryData);
      }
      
      if (amenitiesRes.ok) {
        const amenitiesData = await amenitiesRes.json();
        setAmenities(amenitiesData);
      }

      if (reviewsRes.ok) {
        const reviewsData = await reviewsRes.json();
        setReviews(reviewsData);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = ICON_MAP[iconName] || Sun;
    return <IconComponent className="w-6 h-6" />;
  };

  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    if (url.startsWith('/api/')) return url;
    return url.replace('/uploads/', '/api/uploads/');
  };

  return (
    <Layout>
      {/* HERO - Full Screen Immersive */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Background Slideshow */}
        {gallery.length > 0 ? (
          gallery.map((image, index) => (
            <div
              key={image.id}
              className={`absolute inset-0 transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
            >
              <img
                src={getImageUrl(image.url)}
                alt={image.titolo}
                className="w-full h-full object-cover"
              />
            </div>
          ))
        ) : (
          <div 
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url('https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?auto=format&fit=crop&q=80')` }}
          />
        )}
        
        {/* Gradient Overlays */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/30 to-black/70" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-transparent to-black/40" />
        
        {/* Content */}
        <div className="relative z-10 text-center text-white px-4 max-w-5xl mx-auto">
          <div className="animate-fade-in-up">
            <p className="font-cormorant text-[#C5A059] tracking-[0.3em] uppercase text-sm md:text-base mb-6">
              {t.welcome}
            </p>
            <h1 className="font-cinzel text-5xl md:text-7xl lg:text-8xl mb-2 tracking-wide">
              {t.heroTitle}
            </h1>
            <p className="font-cormorant text-3xl md:text-4xl lg:text-5xl text-[#C5A059] mb-8">
              {t.heroLocation}
            </p>
            <p className="font-manrope text-lg md:text-xl text-gray-200 max-w-2xl mx-auto mb-10">
              {t.heroSubtitle}
            </p>
            
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to="/booking">
                <Button size="lg" className="bg-[#C5A059] hover:bg-[#B08D45] text-white font-cinzel px-8 py-6 text-lg">
                  <Calendar className="w-5 h-5 mr-2" />
                  {t.bookYourStay}
                </Button>
              </Link>
              <Button 
                size="lg" 
                variant="outline" 
                className="border-2 border-white text-white hover:bg-white hover:text-[#1A202C] font-cinzel px-8 py-6 text-lg"
                onClick={() => galleryRef.current?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Play className="w-5 h-5 mr-2" />
                {t.viewAll}
              </Button>
            </div>
          </div>
        </div>

        {/* Slide Indicators */}
        {gallery.length > 1 && (
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
            {gallery.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentSlide(index)}
                className={`h-1 rounded-full transition-all duration-300 ${
                  index === currentSlide ? 'w-8 bg-[#C5A059]' : 'w-4 bg-white/50 hover:bg-white/80'
                }`}
              />
            ))}
          </div>
        )}

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 right-8 hidden md:flex flex-col items-center text-white/60 text-sm">
          <span className="rotate-90 tracking-widest mb-4">SCROLL</span>
          <div className="w-[1px] h-16 bg-gradient-to-b from-white/60 to-transparent" />
        </div>
      </section>

      {/* Location Strip */}
      <div className="bg-[#1A202C] py-5">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap items-center justify-center gap-x-8 gap-y-2">
          <div className="flex items-center gap-2 text-white">
            <MapPin className="w-5 h-5 text-[#C5A059]" />
            <span className="font-manrope">{t.locationBadge}</span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[#C5A059] font-bold text-lg mr-1">9.3</span>
            {[1,2,3,4,5].map(i => (
              <Star key={i} className="w-4 h-4 fill-[#C5A059] text-[#C5A059]" />
            ))}
            <span className="text-gray-400 text-sm ml-2">Booking.com</span>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <section className="py-12 bg-white border-b">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div className="p-4">
              <div className="text-4xl md:text-5xl font-cinzel text-[#C5A059] mb-2">500+</div>
              <div className="font-manrope text-gray-600">{t.stats.guests}</div>
            </div>
            <div className="p-4">
              <div className="text-4xl md:text-5xl font-cinzel text-[#C5A059] mb-2">9.3</div>
              <div className="font-manrope text-gray-600">{t.stats.rating}</div>
            </div>
            <div className="p-4">
              <div className="text-4xl md:text-5xl font-cinzel text-[#C5A059] mb-2">5+</div>
              <div className="font-manrope text-gray-600">{t.stats.yearsExp}</div>
            </div>
            <div className="p-4">
              <div className="text-4xl md:text-5xl font-cinzel text-[#C5A059] mb-2">90+</div>
              <div className="font-manrope text-gray-600">{t.stats.reviews}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section - Masonry Style */}
      <section ref={galleryRef} className="py-20 px-4 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="font-cormorant text-[#C5A059] tracking-[0.3em] uppercase text-sm mb-4">
              {t.discover}
            </p>
            <h2 className="font-cinzel text-4xl md:text-5xl text-[#1A202C] mb-6">
              {t.ourStructure}
            </h2>
            <div className="w-32 h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent mx-auto" />
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Array(8).fill(0).map((_, i) => (
                <div key={i} className={`bg-gray-200 animate-pulse rounded-lg ${i === 0 ? 'col-span-2 row-span-2 aspect-square' : 'aspect-[4/3]'}`} />
              ))}
            </div>
          ) : gallery.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {gallery.slice(0, 8).map((image, index) => (
                <div
                  key={image.id}
                  onClick={() => openLightbox(index)}
                  className={`group relative overflow-hidden rounded-xl cursor-pointer ${
                    index === 0 ? 'col-span-2 row-span-2' : ''
                  }`}
                >
                  <div className={`${index === 0 ? 'aspect-square' : 'aspect-[4/3]'}`}>
                    <img
                      src={getImageUrl(image.url)}
                      alt={image.titolo}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                    <h3 className="font-cinzel text-white text-lg">{image.titolo}</h3>
                  </div>
                </div>
              ))}
              {gallery.length > 8 && (
                <div 
                  onClick={() => openLightbox(8)}
                  className="relative overflow-hidden rounded-xl cursor-pointer bg-[#1A202C] flex items-center justify-center aspect-[4/3]"
                >
                  <div className="text-center text-white">
                    <span className="block text-3xl font-cinzel mb-2">+{gallery.length - 8}</span>
                    <span className="text-sm font-manrope text-gray-400">{t.viewAll}</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-center text-[#4A5568]">{t.noImages}</p>
          )}
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="font-cormorant text-[#C5A059] tracking-[0.3em] uppercase text-sm mb-4">
              {t.whyUs}
            </p>
            <h2 className="font-cinzel text-4xl md:text-5xl text-[#1A202C] mb-4">
              {t.whyUsSubtitle}
            </h2>
            <div className="w-32 h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent mx-auto" />
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="group text-center p-8 rounded-2xl bg-[#F9F9F7] hover:bg-[#1A202C] transition-all duration-500">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#C5A059]/10 group-hover:bg-[#C5A059]/20 flex items-center justify-center transition-colors">
                <MapPin className="w-10 h-10 text-[#C5A059]" />
              </div>
              <h3 className="font-cinzel text-xl text-[#1A202C] group-hover:text-white mb-3 transition-colors">
                {t.reason1Title}
              </h3>
              <p className="font-manrope text-gray-600 group-hover:text-gray-300 transition-colors">
                {t.reason1Desc}
              </p>
            </div>
            
            <div className="group text-center p-8 rounded-2xl bg-[#F9F9F7] hover:bg-[#1A202C] transition-all duration-500">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#C5A059]/10 group-hover:bg-[#C5A059]/20 flex items-center justify-center transition-colors">
                <Award className="w-10 h-10 text-[#C5A059]" />
              </div>
              <h3 className="font-cinzel text-xl text-[#1A202C] group-hover:text-white mb-3 transition-colors">
                {t.reason2Title}
              </h3>
              <p className="font-manrope text-gray-600 group-hover:text-gray-300 transition-colors">
                {t.reason2Desc}
              </p>
            </div>
            
            <div className="group text-center p-8 rounded-2xl bg-[#F9F9F7] hover:bg-[#1A202C] transition-all duration-500">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#C5A059]/10 group-hover:bg-[#C5A059]/20 flex items-center justify-center transition-colors">
                <Users className="w-10 h-10 text-[#C5A059]" />
              </div>
              <h3 className="font-cinzel text-xl text-[#1A202C] group-hover:text-white mb-3 transition-colors">
                {t.reason3Title}
              </h3>
              <p className="font-manrope text-gray-600 group-hover:text-gray-300 transition-colors">
                {t.reason3Desc}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 px-4 bg-[#1A202C]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="font-cormorant text-[#C5A059] tracking-[0.3em] uppercase text-sm mb-4">
              {t.comfort}
            </p>
            <h2 className="font-cinzel text-4xl md:text-5xl text-white mb-4">
              {t.ourServices}
            </h2>
            <div className="w-32 h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent mx-auto" />
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-6">
            {loading ? (
              Array(8).fill(0).map((_, i) => (
                <div key={i} className="bg-white/5 p-6 rounded-xl animate-pulse">
                  <div className="w-12 h-12 bg-white/10 rounded-full mx-auto mb-4"></div>
                  <div className="h-4 bg-white/10 rounded w-3/4 mx-auto"></div>
                </div>
              ))
            ) : amenities.length > 0 ? (
              amenities.map((amenity) => (
                <div 
                  key={amenity.id} 
                  className="group bg-white/5 hover:bg-[#C5A059] p-6 rounded-xl text-center transition-all duration-300 border border-white/10 hover:border-[#C5A059]"
                >
                  <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#C5A059]/20 group-hover:bg-white/20 flex items-center justify-center text-[#C5A059] group-hover:text-white transition-colors">
                    {getIcon(amenity.icona)}
                  </div>
                  <h3 className="font-cinzel text-white text-sm md:text-base">{amenity.nome}</h3>
                </div>
              ))
            ) : (
              <p className="col-span-4 text-center text-gray-400">{t.noServices}</p>
            )}
          </div>
        </div>
      </section>

      {/* Story Section with Side Image */}
      <section className="py-0 bg-white overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          {/* Image Side */}
          <div className="lg:w-1/2 h-[400px] lg:h-auto">
            <img
              src={gallery[1]?.url ? getImageUrl(gallery[1].url) : 'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&q=80'}
              alt="La Maisonette Interior"
              className="w-full h-full object-cover"
            />
          </div>
          
          {/* Text Side */}
          <div className="lg:w-1/2 flex items-center">
            <div className="p-8 md:p-12 lg:p-16 xl:p-20">
              <p className="font-cormorant text-[#C5A059] tracking-[0.3em] uppercase text-sm mb-4">
                {t.ourStory}
              </p>
              <h2 className="font-cinzel text-3xl md:text-4xl text-[#1A202C] mb-8">
                {t.uniqueExperience}
              </h2>
              <p className="font-manrope text-gray-600 leading-relaxed mb-6">
                {t.storyP1}
              </p>
              <p className="font-manrope text-gray-600 leading-relaxed mb-8">
                {t.storyP2}
              </p>
              <Link to="/booking">
                <Button className="bg-[#C5A059] hover:bg-[#B08D45] text-white font-cinzel">
                  {t.bookYourStay}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Reviews/Testimonials Section */}
      {reviews.length > 0 && (
        <section className="py-20 px-4 bg-[#1A202C] overflow-hidden">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <p className="font-cormorant text-[#C5A059] tracking-[0.3em] uppercase text-sm mb-4">
                {t.guestsSay}
              </p>
              <h2 className="font-cinzel text-4xl md:text-5xl text-white mb-4">
                {t.verifiedReviews}
              </h2>
              <div className="w-32 h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent mx-auto" />
            </div>

            {/* Reviews Carousel */}
            <div className="relative">
              <div className="overflow-hidden">
                <div 
                  className="flex transition-transform duration-700 ease-in-out"
                  style={{ transform: `translateX(-${currentReview * 100}%)` }}
                >
                  {reviews.map((review) => (
                    <div key={review.id} className="w-full flex-shrink-0 px-4">
                      <div className="max-w-3xl mx-auto text-center">
                        {/* Quote Icon */}
                        <div className="w-16 h-16 mx-auto mb-8 rounded-full bg-[#C5A059]/20 flex items-center justify-center">
                          <svg className="w-8 h-8 text-[#C5A059]" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                          </svg>
                        </div>

                        {/* Review Text */}
                        <p className="font-manrope text-xl md:text-2xl text-gray-300 italic leading-relaxed mb-8">
                          &ldquo;{language === 'en' && review.testo_en ? review.testo_en : review.testo}&rdquo;
                        </p>

                        {/* Rating Stars */}
                        <div className="flex items-center justify-center gap-1 mb-6">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i} 
                              className={`w-5 h-5 ${
                                i < Math.floor(review.rating / 2) 
                                  ? 'fill-[#C5A059] text-[#C5A059]' 
                                  : 'text-gray-600'
                              }`} 
                            />
                          ))}
                          <span className="ml-2 text-[#C5A059] font-bold">{review.rating}/10</span>
                        </div>

                        {/* Author Info */}
                        <div className="flex items-center justify-center gap-4">
                          {review.avatar_url ? (
                            <img 
                              src={review.avatar_url} 
                              alt={review.nome_ospite}
                              className="w-14 h-14 rounded-full object-cover border-2 border-[#C5A059]"
                            />
                          ) : (
                            <div className="w-14 h-14 rounded-full bg-[#C5A059] flex items-center justify-center">
                              <span className="text-xl font-bold text-white">
                                {review.nome_ospite.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                          <div className="text-left">
                            <p className="font-cinzel text-white text-lg">{review.nome_ospite}</p>
                            <div className="flex items-center gap-2 text-sm text-gray-400">
                              {review.nazionalita && <span>{review.nazionalita}</span>}
                              {review.nazionalita && review.data_soggiorno && <span>•</span>}
                              {review.data_soggiorno && <span>{review.data_soggiorno}</span>}
                            </div>
                            <div className="flex items-center gap-1 mt-1">
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                review.fonte === 'Booking.com' ? 'bg-blue-600' :
                                review.fonte === 'Google' ? 'bg-red-600' :
                                review.fonte === 'TripAdvisor' ? 'bg-green-600' :
                                'bg-gray-600'
                              } text-white`}>
                                {review.fonte}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Navigation Dots */}
              {reviews.length > 1 && (
                <div className="flex justify-center gap-2 mt-10">
                  {reviews.map((_, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentReview(index)}
                      className={`h-2 rounded-full transition-all duration-300 ${
                        index === currentReview ? 'w-8 bg-[#C5A059]' : 'w-2 bg-white/30 hover:bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              )}

              {/* Navigation Arrows */}
              {reviews.length > 1 && (
                <>
                  <button
                    onClick={() => setCurrentReview((prev) => (prev - 1 + reviews.length) % reviews.length)}
                    className="absolute left-0 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                  >
                    <ChevronLeft className="w-6 h-6" />
                  </button>
                  <button
                    onClick={() => setCurrentReview((prev) => (prev + 1) % reviews.length)}
                    className="absolute right-0 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white transition-colors"
                  >
                    <ChevronRight className="w-6 h-6" />
                  </button>
                </>
              )}
            </div>

            {/* Booking.com Badge */}
            <div className="mt-12 text-center">
              <div className="inline-flex items-center gap-3 bg-white/5 rounded-full px-6 py-3">
                <div className="flex items-center gap-1">
                  <Star className="w-5 h-5 fill-[#C5A059] text-[#C5A059]" />
                  <span className="text-2xl font-bold text-white">9.3</span>
                </div>
                <div className="h-8 w-[1px] bg-white/20" />
                <div className="text-left">
                  <p className="text-white text-sm font-semibold">Booking.com</p>
                  <p className="text-gray-400 text-xs">90+ {language === 'it' ? 'recensioni' : 'reviews'}</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Map Section */}
      <section className="py-20 px-4 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <p className="font-cormorant text-[#C5A059] tracking-[0.3em] uppercase text-sm mb-4">
              {t.whereWeAre}
            </p>
            <h2 className="font-cinzel text-4xl md:text-5xl text-[#1A202C] mb-4">
              {t.ourLocation}
            </h2>
            <div className="w-32 h-[2px] bg-gradient-to-r from-transparent via-[#C5A059] to-transparent mx-auto mb-6" />
            <p className="font-manrope text-gray-600 max-w-2xl mx-auto">
              {t.locationDesc}
            </p>
          </div>

          <div className="rounded-2xl overflow-hidden shadow-2xl">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3037.8474!2d15.0067!3d40.4219!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x133bc5b5a5a5a5a5%3A0x5a5a5a5a5a5a5a5a!2sCapaccio%20Paestum%2C%20SA!5e0!3m2!1sit!2sit!4v1234567890"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="La Maisonette di Paestum - Mappa"
            ></iframe>
          </div>

          <div className="mt-10 grid md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-xl shadow-sm text-center hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
                <span className="text-2xl">🏛️</span>
              </div>
              <h3 className="font-cinzel text-lg text-[#1A202C] mb-2">{t.temples}</h3>
              <p className="font-manrope text-[#C5A059] font-semibold">{t.walkMin}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm text-center hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
                <span className="text-2xl">🏖️</span>
              </div>
              <h3 className="font-cinzel text-lg text-[#1A202C] mb-2">{t.beach}</h3>
              <p className="font-manrope text-[#C5A059] font-semibold">{t.carMin10}</p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm text-center hover:shadow-lg transition-shadow">
              <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
                <span className="text-2xl">🚗</span>
              </div>
              <h3 className="font-cinzel text-lg text-[#1A202C] mb-2">{t.salerno}</h3>
              <p className="font-manrope text-[#C5A059] font-semibold">{t.carMin30}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative py-24 px-4 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{ 
            backgroundImage: `url('${gallery[gallery.length - 1]?.url ? getImageUrl(gallery[gallery.length - 1].url) : 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?auto=format&fit=crop&q=80'}')` 
          }}
        />
        <div className="absolute inset-0 bg-[#1A202C]/80" />
        
        <div className="relative z-10 max-w-3xl mx-auto text-center">
          <h2 className="font-cinzel text-3xl md:text-5xl text-white mb-6">
            {t.ctaTitle}
          </h2>
          <p className="font-manrope text-xl text-gray-300 mb-10">
            {t.ctaSubtitle}
          </p>
          <Link to="/booking">
            <Button size="lg" className="bg-[#C5A059] hover:bg-[#B08D45] text-white font-cinzel px-10 py-6 text-lg">
              <Calendar className="w-5 h-5 mr-2" />
              {t.checkAvailability}
            </Button>
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div 
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
        >
          <button
            className="absolute top-6 right-6 text-white/70 hover:text-white z-50"
            onClick={() => setLightboxOpen(false)}
          >
            <X className="w-8 h-8" />
          </button>
          
          <button
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white z-50"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev - 1 + gallery.length) % gallery.length); }}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          
          <button
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white z-50"
            onClick={(e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev + 1) % gallery.length); }}
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <div className="max-w-6xl max-h-[90vh] p-4" onClick={(e) => e.stopPropagation()}>
            <img
              src={getImageUrl(gallery[lightboxIndex]?.url)}
              alt={gallery[lightboxIndex]?.titolo}
              className="max-w-full max-h-[80vh] object-contain mx-auto rounded-lg"
            />
            <div className="text-center mt-4">
              <p className="text-white font-cinzel text-xl">{gallery[lightboxIndex]?.titolo}</p>
              <p className="text-gray-400 text-sm mt-1">{lightboxIndex + 1} / {gallery.length}</p>
            </div>
          </div>
        </div>
      )}

      {/* Custom styles */}
      <style>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fade-in-up 1s ease-out forwards;
        }
      `}</style>
    </Layout>
  );
}
